import { useEffect } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import useCampaigns from '@/hooks/useCampaigns';
import useTeamMembers from '@/hooks/useTeamMembers';
import { ICalendar } from '@/types';
import axiosClient from '@/utils/axiosClient';
import Drawer from '../Modals/Drawer';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const schema = z.object({
  name: z.string().nonempty({ message: 'Name is required' }),
  description: z.string().nonempty({ message: 'Description is required' }),
  color: z.string().nonempty({ message: 'Color is required' }),
  timezone: z.string().nonempty({ message: 'Timezone is required' }),
  user_ids: z.array(z.string()).optional(),
  campaign_ids: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof schema>;

function mapDefaultValues(data: ICalendar) {
  return {
    name: data.name,
    description: data.description,
    color: data.color,
    timezone: data.timezone,
    user_ids: data.user_ids,
    campaign_ids: data.campaign_ids,
  };
}

interface DrawerProps {
  calendar: ICalendar;
}

const colors = [
  {
    name: 'Red',
    value: 'red',
  },
  {
    name: 'Blue',
    value: 'blue',
  },
  {
    name: 'Green',
    value: 'green',
  },
  {
    name: 'Yellow',
    value: 'yellow',
  },
  {
    name: 'Purple',
    value: 'purple',
  },
  {
    name: 'Pink',
    value: 'pink',
  },
  {
    name: 'Gray',
    value: 'gray',
  },
];

const usTimezones = [
  {
    name: 'Eastern Time',
    value: 'America/New_York',
  },
  {
    name: 'Central Time',
    value: 'America/Chicago',
  },
  {
    name: 'Mountain Time',
    value: 'America/Denver',
  },
  {
    name: 'Pacific Time',
    value: 'America/Los_Angeles',
  },
  {
    name: 'Alaska Time',
    value: 'America/Anchorage',
  },
  {
    name: 'Hawaii Time',
    value: 'Pacific/Honolulu',
  },
];

const CalendarDrawer = NiceModal.create(({ calendar }: DrawerProps) => {
  const modal = useModal('calendar-drawer');
  const queryClient = useQueryClient();

  const teamMembers = useTeamMembers();
  const campaigns = useCampaigns();

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      color: '',
      timezone: browserTimezone,
      user_ids: [],
      campaign_ids: [],
    },
  });

  useEffect(() => {
    if (calendar) {
      const formValues = mapDefaultValues(calendar);
      form.reset(formValues as any);
    }
  }, [calendar, form]);

  const onSubmit = async (data: FormData) => {
    try {
      if (calendar) {
        await axiosClient.put(`v1/calendars/${calendar.id}`, data);
        queryClient.invalidateQueries(`v1/calendars/${calendar.id}`);
      } else {
        await axiosClient.post('v1/calendars', data);
      }

      queryClient.invalidateQueries('v1/calendars');
      modal.hide();
    } catch (error) {
      console.error('Error saving contact list:', error);
    }
  };

  return (
    <Drawer modal={modal} title="Calendars">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col justify-center gap-4 p-4">
          <div className="flex flex-col gap-4">
            {/* Existing form fields */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a color" />
                      </SelectTrigger>
                      <SelectContent>
                        {colors.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{
                                  backgroundColor: color.value,
                                }}
                              />
                              {color.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Timezone</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a default timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {usTimezones.map((timezone) => (
                          <SelectItem key={timezone.value} value={timezone.value}>
                            {timezone.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 5. Add the user_ids field */}
            <FormField
              control={form.control}
              name="user_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subscribers</FormLabel>
                  <FormControl>
                    <div className="flex flex-col space-y-2 overflow-y-auto max-h-60">
                      {teamMembers.data && teamMembers.data.length > 0 ? (
                        teamMembers.data.map((member) => {
                          const userId = member.user.id;
                          const isChecked = (field.value || []).includes(userId);

                          return (
                            <Label key={member.id} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  const valueArray = field.value || [];
                                  if (checked) {
                                    field.onChange([...valueArray, userId]);
                                  } else {
                                    field.onChange(valueArray.filter((id) => id !== userId));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 form-checkbox"
                              />
                              <span>{`${member.user.first_name} ${member.user.last_name} (${member.user.email})`}</span>
                            </Label>
                          );
                        })
                      ) : (
                        <p>No team members available.</p>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="campaign_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Campaigns</FormLabel>
                  <FormControl>
                    <div className="flex flex-col space-y-2 overflow-y-auto max-h-60">
                      {campaigns.data && campaigns.data.length > 0 ? (
                        campaigns.data.map((campaign) => {
                          const campaignId = campaign.id;
                          const isChecked = (field.value || []).includes(campaignId);

                          return (
                            <Label key={campaignId} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  const valueArray = field.value || [];
                                  if (checked) {
                                    field.onChange([...valueArray, campaignId]);
                                  } else {
                                    field.onChange(valueArray.filter((id) => id !== campaignId));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 form-checkbox"
                              />
                              <span>{campaign.name}</span>
                            </Label>
                          );
                        })
                      ) : (
                        <p>No campaigns created yet.</p>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="relative inline-flex items-center justify-end w-full gap-2 mt-2">
            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full sm:w-fit">
              <Save />
              {form.formState.isSubmitting ? 'Saving...' : 'Save Calendar'}
            </Button>
          </div>
        </form>
      </Form>
    </Drawer>
  );
});

export default CalendarDrawer;
